import { Cart } from 'shared/types/cart';
import { Product, Variant } from 'shared/types/product';

const sendEvent = (eventName: string, eventData: unknown) => {
  console.log(`GA4 event ${eventName}`, eventData);
  window.gtag('event', eventName, eventData);
};

const sendCartEvent = (
  eventName: string,
  product: Pick<Product, 'name' | 'productId'>,
  variant: Variant,
  quantity: number,
) => {
  const eventData = {
    ecommerce: {
      currency: 'EUR',
      value: ((variant?.price?.centAmount || 0) * (quantity || 1), 0) / 100,
      items: [
        {
          item_id: variant.sku, // ID unico del prodotto
          item_name: product.name || product.productId || '', // Nome del prodotto
          item_category: variant.attributes?.gender, // Genere del prodotto
          item_category2: variant.attributes?.typology, // Tipo prodotto
          item_category3: variant.attributes?.season, // Stagione
          item_brand: variant.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Numero della calzatura
          price: variant?.price?.centAmount && variant.price?.centAmount / 100, // Prezzo del prodotto
          quantity, // Quantità aggiunta
        },
      ],
    },
  };
  sendEvent(eventName, eventData);
};

export const sendGa4AddToCartEvent = sendCartEvent.bind(null, 'add_to_cart');

export const sendGa4RemoveFromCartEvent = sendCartEvent.bind(null, 'remove_from_cart');

export const sendGa4AddShippingInfoEvent = () => {
  const eventName = 'add_shipping_info';
  // TODO ? Shipping tiers ? Checkoutflow integration ?
};

export const sendGa4AddPaymentInfoEvent = () => {
  const eventName = 'add_payment_info';
  // TODO ? Payment types ? Checkoutflow integration ?
};

export const sendGa4BeginCheckoutEvent = (cart: Cart) => {
  const eventName = 'begin_checkout';
  const eventData = {
    ecommerce: {
      currency: 'EUR',
      value: cart.sum, // cart total
      coupon: cart.discountCodes?.map((discountCode) => discountCode.code).join(','),
      items: cart.lineItems?.map((lineItem) => ({
        item_id: lineItem.productId, // ID unico del prodotto
        item_name: lineItem.name, // Nome del prodotto
        item_category: lineItem.variant?.attributes?.gender, // Genere del prodotto
        item_category2: lineItem.variant?.attributes?.typology, // Tipo prodotto
        item_category3: lineItem.variant?.attributes?.season, // Stagione
        item_brand: lineItem.variant?.attributes?.brand, // Marca
        item_variant: `${lineItem.variant?.attributes?.colourGroupCode} / ${lineItem.variant?.attributes?.size}`, // Numero della calzatura
        price: lineItem.totalPrice?.centAmount && lineItem.totalPrice?.centAmount / 100, // Prezzo del prodotto
        quantity: lineItem.count, // Quantità nel carrello
      })),
    },
  };
  sendEvent(eventName, eventData);
};

export const sendGa4AddToWishlistEvent = (productId?: string, productName?: string, variant?: Variant) => {
  const eventName = 'add_to_wishlist';
  const eventData = {
    ecommerce: {
      currency: 'EUR',
      value: (variant?.price?.centAmount || 0) / 100,
      user_id: '', // ID dell'utente (opzionale)
      items: [
        {
          item_id: productId, // ID unico del prodotto
          item_name: productName, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere del prodotto
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Numero della calzatura
          price: variant?.price?.centAmount && variant.price?.centAmount / 100, // Prezzo del prodotto
          quantity: 1, // Quantità aggiunta
        },
      ],
    },
  };
  sendEvent(eventName, eventData);
};

// TODO:
export const sendGa4InvioFormContattiEvent = () => {
  const eventName = 'invio_form_contatti';
  sendEvent(eventName, {});
};

// TODO:
export const sendGa4AddInvioFormNewsletterEvent = () => {
  const eventName = 'invio_form_newsletter';
  sendEvent(eventName, {});
};

export const sendPurchaseEvent = () => {
  const eventName = 'purchase';
  // TODO: Checkoutflow integration ?
};

// TODO: fired multiple times
export const sendGa4ViewCartEvent = (cart: Cart) => {
  const eventName = 'view_cart';
  const eventData = {
    ecommerce: {
      currency: 'EUR',
      value: cart.sum, // cart total
      items: cart.lineItems?.map((lineItem) => ({
        item_id: lineItem.productId, // ID unico del prodotto
        item_name: lineItem.name, // Nome del prodotto
        item_category: lineItem.variant?.attributes?.gender, // Genere del prodotto
        item_category2: lineItem.variant?.attributes?.typology, // Tipo prodotto
        item_category3: lineItem.variant?.attributes?.season, // Stagione
        item_brand: lineItem.variant?.attributes?.brand, // Marca
        item_variant: `${lineItem.variant?.attributes?.colourGroupCode} / ${lineItem.variant?.attributes?.size}`, // Numero della calzatura
        price: lineItem.totalPrice?.centAmount && lineItem.totalPrice?.centAmount / 100, // Prezzo del prodotto
        quantity: lineItem.count, // Quantità nel carrello
      })),
    },
  };
  sendEvent(eventName, eventData);
};

export const sendGa4ViewItemListEvent = () => {
  const eventName = 'view_item_list';
  // TODO ?
};

export const sendGa4ViewItemEvent = (product: Product, variant: Variant) => {
  const eventName = 'view_item';
  const eventData = {
    ecommerce: {
      currency: 'EUR',
      value: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
      items: [
        {
          item_id: product.productId, // ID unico del prodotto
          item_name: product.name, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Variante
          price: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
          quantity: 1, // Quantità
        },
      ],
    },
  };
  sendEvent(eventName, eventData);
};

export const sendGa4SelectItemEvent = (product: Product, variant: Variant, index: number) => {
  const eventName = 'select_item';
  const eventData = {
    ecommerce: {
      items: [
        {
          item_id: product.productId, // ID unico del prodotto
          item_name: product.name, // Nome del prodotto
          item_category: variant?.attributes?.gender, // Genere
          item_category2: variant?.attributes?.typology, // Tipo prodotto
          item_category3: variant?.attributes?.season, // Stagione
          item_brand: variant?.attributes?.brand, // Marca
          item_variant: `${variant?.attributes?.colourGroupCode} / ${variant?.attributes?.size}`, // Variante
          price: variant.price?.centAmount && variant.price?.centAmount / 100, // Prezzo
          quantity: undefined, // Quantità (se applicabile)
          index: index + 1, // Posizione nell'elenco
          list_name: 'PLP', // Nome della lista
          creative_name: undefined, // Nome creativo (opzionale)
          creative_slot: undefined, // Posizione creativa (opzionale)
        },
      ],
    },
  };
  sendEvent(eventName, eventData);
};

export const sendGa4ViewPromotionEvent = () => {
  const eventName = 'view_promotion';
  // TODO ?
};
